"use client";

import { useEffect, useRef, useState } from "react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
type Stat = {
  value: number;
  suffix: string;
  prefix: string;
  display: string;
  label: string;
  caption: string;
};
const stats: Stat[] = [{
  value: 1250,
  suffix: "+",
  prefix: "",
  display: "1,250+",
  label: "Projects Completed",
  caption: "Globally"
}, {
  value: 100,
  suffix: "%",
  prefix: "",
  display: "100%",
  label: "Compliance Rate",
  caption: "Regulatory standards met"
}, {
  value: 15,
  suffix: "+",
  prefix: "",
  display: "15+",
  label: "Years of Experience",
  caption: "Industrial fire safety"
}];
function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}
function formatValue(stat: Stat, progress: number): string {
  const current = stat.value * progress;
  let numStr: string;
  if (stat.value >= 1000) {
    numStr = Math.round(current).toLocaleString();
  } else if (stat.value < 10 && !Number.isInteger(stat.value)) {
    numStr = current.toFixed(1);
  } else {
    numStr = Math.round(current).toString();
  }
  return `${stat.prefix}${numStr}${stat.suffix}`;
}
function CountUpNumeral({
  stat,
  start
}: {
  stat: Stat;
  start: boolean;
}) {
  const [display, setDisplay] = useState<string>(formatValue(stat, 0));
  const rafRef = useRef<number | null>(null);
  useEffect(() => {
    if (!start) return;
    const duration = 1400;
    const startTime = performance.now();
    const tick = (now: number) => {
      const elapsed = now - startTime;
      const t = Math.min(elapsed / duration, 1);
      const eased = easeOutCubic(t);
      setDisplay(formatValue(stat, eased));
      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        setDisplay(stat.display);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [start, stat]);
  return <span className="font-mono font-medium tracking-tight leading-none" style={{
    color: "var(--accent-foreground)",
    fontSize: "clamp(2.25rem, 4vw, 3.5rem)"
  }}>
      {display}
    </span>;
}
export default function Reliability() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.2
  });
  return <section data-section-id="15706" id="quantified-reliability" className="dark bg-background text-foreground py-20 md:py-28">
      <div className="container mx-auto px-5 md:px-10 max-w-[1280px]">
        <div ref={ref} className={`mx-auto max-w-[720px] text-center transition-all duration-[640ms] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
        transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)"
      }}>
          <p className="text-xs font-semibold uppercase font-default" style={{
          letterSpacing: "0.14em",
          color: "var(--muted-foreground)"
        }}>
            Engineering Metrics
          </p>
          <div className="mt-3 mx-auto" style={{
          width: "48px",
          height: "1px",
          backgroundColor: "var(--border)"
        }} aria-hidden="true" />
          <h2 className="font-serif font-default mt-8 font-normal text-foreground" style={{
          fontSize: "clamp(2rem, 3.6vw, 3.25rem)",
          letterSpacing: "-0.018em",
          lineHeight: 1.08
        }}>
            {"Data-driven"}
            <span style={{
            color: "var(--accent-foreground)"
          }}>{", "}</span>
            <span>{"industrial resilience."}</span>
          </h2>
        </div>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-y-8 gap-x-12">
          {stats.map((stat, i) => <div key={i} data-index={i} className={`pl-6 py-2 border-l transition-all duration-[640ms] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          borderColor: "var(--border)",
          transitionDelay: `${120 + i * 80}ms`,
          transitionTimingFunction: "cubic-bezier(0.22, 0.61, 0.36, 1)"
        }}>
              <div className="block">
                <CountUpNumeral stat={stat} start={isVisible} />
              </div>
              <p className="mt-3 font-default text-foreground" style={{
            fontSize: "15px",
            fontWeight: 440,
            lineHeight: 1.4
          }}>
                {stat.label}
              </p>
              <p className="mt-1 font-default" style={{
            fontSize: "0.8125rem",
            lineHeight: 1.45,
            color: "var(--muted-foreground)",
            letterSpacing: "0.005em"
          }}>
                {stat.caption}
              </p>
            </div>)}
        </div>
      </div>
    </section>;
}